import { useState, useEffect, useCallback } from 'react';
import { Box, Container, useBreakpointValue } from '@chakra-ui/react';

import { CASTracker } from 'shared-components';

import Checklist from './checklist';
import Documents from './documents';
import FinancialDocs from './financialDocs';
import Interview from './interview';
import VisaPrepForm from './visaPrepForm';
import EnrolmentStatus from './enrolmentStatus';
import ScheduledInterviews from './scheduledInterviews';

import CASTrackerReminder from './casTrackerReminder';
import { useNavigate, useParams } from 'react-router-dom';
import { Loading } from '../../atoms/loading';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { saveHasViewedBypassWelcomeMessage } from '../../../store/student/actions';
import PreDepartureDocuments from './followUpDocuments/preDepartureDocs';
import AfterArrivalDocuments from './followUpDocuments/afterTravelDocs';
import { Status, ISectionProps, SectionId } from '../../molecules/Sidebar/types';
import Sidebar from '../../molecules/Sidebar';
import PrevNextButtons from '../../atoms/PrevNextButtons';
import PrevNextIconButtons from '../../atoms/PrevNextIconButtons';
import CASBypassModal from '../../molecules/CASBypassModal';
import { getStudentRecord } from '../../../store/student/actions';
import { closeModal, openDocumentModal, toggleTaskMenu, updateActiveSectionId } from '../../../store/ui/actions';
import { logout } from '../../../store/auth/actions';
import { useDashboardData } from './useDashboardData';
import { useSession } from 'shared-components/authentication';

const Dashboard = () => {
  const {
    sections,
    showCompleteModal,
    openModal,
    taskMenuOpen,
    activeSectionId,
    initialActiveSectionId,
    completeLabel,
    completeSubLabel,
    student,
    isStudentLoaded,
    summary,
    isTrackerVisible,
    student_number,
    student_name
  } = useDashboardData();

  const navigate = useNavigate();
  const session = useSession();
  const [modalState, setModalState] = useState(false);

  const goToLanding = useCallback(
    (intakeId: string, offerId: string) => navigate(`/${intakeId}/${offerId}/landing`),
    [navigate]
  );
  const { intakeId, offerId } = useParams<{
    intakeId: string;
    offerId: string;
  }>();
  const showBypassWelcomeMessage = useSelector(
    (state: ApplicationState) => !state.student.offer.hasViewedBypassWelcomeMessage && state.student.offer.casBypass
  );

  const dispatch = useDispatch();
  const openCompleteModal = useCallback(() => {
    dispatch(openDocumentModal('complete', null, null, null, false));
  }, [dispatch]);
  const updateSectionId = useCallback((sectionId: SectionId) => dispatch(updateActiveSectionId(sectionId)), [dispatch]);
  const getStudent = useCallback(
    (intakeId: string, offerId: string) => dispatch(getStudentRecord(intakeId, offerId)),
    [dispatch]
  );

  useEffect(() => {
    if (isStudentLoaded && showCompleteModal && modalState === false) {
      openCompleteModal();
    }
  }, [showCompleteModal, modalState, openCompleteModal, isStudentLoaded]);

  useEffect(() => {
    updateSectionId(initialActiveSectionId);
  }, [updateSectionId, initialActiveSectionId]);

  useEffect(() => {
    if (
      student.offer.intakeId &&
      student.offer.offerId &&
      (student.offer.intakeId !== intakeId || student.offer.offerId !== offerId || !isStudentLoaded)
    ) {
      getStudent(intakeId, offerId);
    }
  }, [getStudent, student.offer, isStudentLoaded, intakeId, offerId]);

  useEffect(() => {
    if (isStudentLoaded && !student.offer?.isPISectionStarted) {
      goToLanding(student.offer.intakeId, student.offer.offerId);
    }
  }, [isStudentLoaded, student, goToLanding, intakeId, offerId]);

  const onCloseModal = () => {
    setModalState(true); // flag that the user has already closed the modal
    dispatch(closeModal());
  };

  const onSectionSelect = (sectionId: SectionId) => {
    const section = sections.find(section => section.id === sectionId);
    if (!section || !section.clickable) {
      return;
    }

    dispatch(toggleTaskMenu(!taskMenuOpen));

    updateSectionId(sectionId);
  };

  const moveUp = () => {
    move(i => i - 1);
  };

  const moveDown = () => {
    move(i => i + 1);
  };

  const move = (fn: (i: number) => number) => {
    const currentIndex = sections.findIndex(section => section.id === activeSectionId);
    const nextIndex = findNextIndex(currentIndex, fn, sections);
    const nextActiveSection = sections.find((_, i) => i === nextIndex);
    if (nextActiveSection) updateSectionId(nextActiveSection.id);
  };

  const findNextIndex = (currentIndex: number, fn: (i: number) => number, sections: Array<ISectionProps>): number => {
    let nextIndex = fn(currentIndex) % sections.length;
    if (nextIndex < 0) nextIndex += sections.length;
    if (sections[nextIndex].status === Status.Disabled) {
      return findNextIndex(nextIndex, fn, sections);
    }
    return nextIndex;
  };

  const getSection = (fn: (i: number) => number) => {
    const currentIndex = sections.findIndex(section => section.id === activeSectionId);
    const nextIndex = findNextIndex(currentIndex, fn, sections);
    return sections[nextIndex];
  };

  const contentPage = () => {
    switch (activeSectionId) {
      case SectionId.CHECKLIST:
        return <Checklist />;
      case SectionId.GENERAL_DOC:
        return <Documents />;
      case SectionId.FINANCE_DOC:
        return <FinancialDocs />;
      case SectionId.PRE_CAS_INTERVIEW:
        return <Interview />;
      case SectionId.VISA_PREP:
        return <VisaPrepForm />;
      case SectionId.ENROLMENT:
        return <EnrolmentStatus />;
      case SectionId.SCHEDULED_INTERVIEWS:
        return <ScheduledInterviews />;
      case SectionId.PRE_DEPARTURE_DOCS:
        return <PreDepartureDocuments />;
      case SectionId.AFTER_TRAVEL_DOCS:
        return <AfterArrivalDocuments />;
      default:
        return <Checklist />;
    }
  };
  const currentActiveSection: ISectionProps | undefined = sections.find(section => section.id === activeSectionId);
  const hasPreviousSection = currentActiveSection ? currentActiveSection.id !== SectionId.CHECKLIST : false;
  const nextSection = getSection(i => i + 1);
  let hasNextSection = false;
  if (currentActiveSection && nextSection.id !== currentActiveSection.id) {
    const clickableSections = sections.filter(section => section.clickable);
    const lastClickableSection = clickableSections[clickableSections.length - 1];
    hasNextSection = lastClickableSection.id !== currentActiveSection.id;
  }

  const isLargeScreen = useBreakpointValue({ base: false, lg: true });

  const onLogout = async () => {
    dispatch(logout());
    await session.onLogout();
  };

  if (isStudentLoaded) {
    switch (openModal?.sponsorType) {
      case 'complete': {
        return <CASTracker onClose={onCloseModal} summary={summary} />;
      }
      default: {
        return (
          <>
            <Sidebar
              onLogout={onLogout}
              student_number={student_number}
              student_name={student_name}
              sections={sections}
              activeSectionId={activeSectionId}
              completeLabel={completeLabel}
              completeDesc={completeSubLabel}
              onSectionSelect={onSectionSelect}
              pageButtons={
                <PrevNextIconButtons
                  onNext={hasNextSection ? moveDown : undefined}
                  onPrevious={hasPreviousSection ? moveUp : undefined}
                  nextLabel="Next task"
                  previousLabel="Previous task"
                  complete={hasNextSection}
                />
              }
            />

            <Box
              id="dashboardContent"
              ml={{ base: 0, lg: '22rem', xl: '24rem' }}
              mt={{ base: 14, lg: 0 }}
              pt={{ base: 8, lg: 10 }}
              pb={10}
            >
              <Container as="main" id="main" ml={{ base: 'auto', lg: 6, xl: 16 }}>
                {isTrackerVisible ? <CASTrackerReminder onClick={openCompleteModal} summary={summary} /> : null}

                {contentPage()}

                {isLargeScreen ? (
                  <PrevNextButtons
                    isNextSecondary
                    onNext={hasNextSection ? moveDown : undefined}
                    onPrevious={hasPreviousSection ? moveUp : undefined}
                    nextLabel="Next task"
                    previousLabel="Previous task"
                    complete={hasNextSection}
                  />
                ) : null}
              </Container>
            </Box>

            {showBypassWelcomeMessage ? (
              <CASBypassModal onCloseComplete={() => dispatch(saveHasViewedBypassWelcomeMessage())} />
            ) : null}
          </>
        );
      }
    }
  } else {
    return <Loading show={true} apiCallsInProgress={[]} />;
  }
};

export default Dashboard;
